import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { quoteService } from '../services/quote.service';

// TOUS les thunks (existants + nouveaux)
export const fetchQuotes = createAsyncThunk(
  'quote/fetchQuotes',
  async (_, { rejectWithValue }) => {
    try {
      const response = await quoteService.getAllQuotes();
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Erreur lors de la récupération des devis');
    }
  }
);

export const fetchQuoteById = createAsyncThunk(
  'quote/fetchQuoteById',
  async (id, { rejectWithValue }) => {
    try {
      return await quoteService.getQuoteById(id);
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Erreur lors de la récupération du devis');
    }
  }
);

export const createQuote = createAsyncThunk(
  'quote/createQuote',
  async (quote, { rejectWithValue }) => {
    try {
      const result = await quoteService.createQuote(quote);
      return result;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Erreur lors de la création du devis');
    }
  }
);

export const updateQuote = createAsyncThunk(
  'quote/updateQuote',
  async ({ id, quoteData }, { rejectWithValue }) => {
    try {
      const result = await quoteService.updateQuote(id, quoteData);
      return result;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Erreur lors de la mise à jour du devis');
    }
  }
);

export const softDeleteQuote = createAsyncThunk(
  'quote/softDeleteQuote',
  async (id, { rejectWithValue }) => {
    try {
      await quoteService.softDeleteQuote(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Erreur lors de la suppression du devis');
    }
  }
);

export const hardDeleteQuote = createAsyncThunk(
  'quote/hardDeleteQuote',
  async (id, { rejectWithValue }) => {
    try {
      await quoteService.hardDeleteQuote(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Erreur lors de la suppression définitive du devis');
    }
  }
);

export const fetchActiveQuotes = createAsyncThunk(
  'quote/fetchActiveQuotes',
  async (_, { rejectWithValue }) => {
    try {
      return await quoteService.getActiveQuotes();
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Erreur lors de la récupération des devis actifs');
    }
  }
);

export const fetchInactiveQuotes = createAsyncThunk(
  'quote/fetchInactiveQuotes',
  async (_, { rejectWithValue }) => {
    try {
      return await quoteService.getInactiveQuotes();
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Erreur lors de la récupération des devis inactifs');
    }
  }
);

export const fetchQuotesByCustomerId = createAsyncThunk(
  'quote/fetchQuotesByCustomerId',
  async (customerId, { rejectWithValue }) => {
    try {
      return await quoteService.getQuotesByCustomerId(customerId);
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Erreur lors de la récupération des devis du client');
    }
  }
);

export const fetchQuotesByCustomerName = createAsyncThunk(
  'quote/fetchQuotesByCustomerName',
  async (name, { rejectWithValue }) => {
    try {
      return await quoteService.getQuotesByCustomerName(name);
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Erreur lors de la récupération des devis par nom client');
    }
  }
);

export const fetchQuotesByDateRange = createAsyncThunk(
  'quote/fetchQuotesByDateRange',
  async (filter, { rejectWithValue }) => {
    try {
      return await quoteService.getQuotesByDateRange(filter);
    } catch (error) {
      return rejectWithValue(
        error.response?.data || 'Erreur lors de la récupération des devis par date'
      );
    }
  }
);

export const fetchQuotesPaginated = createAsyncThunk(
  'quote/fetchQuotesPaginated',
  async (filter, { rejectWithValue }) => {
    try {
      return await quoteService.getQuotesPaginated(filter);
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Erreur lors de la récupération paginée des devis');
    }
  }
);

// NOUVEAUX THUNKS
export const publishQuote = createAsyncThunk(
  'quote/publishQuote',
  async (id, { rejectWithValue }) => {
    try {
      const response = await quoteService.publishQuote(id);
      return { id, ...response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Erreur lors de la publication du devis');
    }
  }
);

export const approveQuote = createAsyncThunk(
  'quote/approveQuote',
  async (id, { rejectWithValue }) => {
    try {
      const response = await quoteService.approveQuote(id);
      return { id, ...response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Erreur lors de l\'approbation du devis');
    }
  }
);

export const cancelQuote = createAsyncThunk(
  'quote/cancelQuote',
  async (id, { rejectWithValue }) => {
    try {
      const response = await quoteService.cancelQuote(id);
      return { id, ...response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Erreur lors de l\'annulation du devis');
    }
  }
);

export const transformQuoteToOrder = createAsyncThunk(
  'quote/transformQuoteToOrder',
  async (id, { rejectWithValue }) => {
    try {
      const response = await quoteService.transformQuoteToOrder(id);
      return { id, ...response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Erreur lors de la transformation en commande');
    }
  }
);

// Initial state
const initialState = {
  quotes: [],
  totalCount: 0,
  selectedQuote: null,
  loading: false,
  loadingById: false,
  error: null,
  showModal: false,
  showDeleteModal: false,
  searchQuery: '',
  searchResults: [],
  searchLoading: false,
  actionStatus: 'idle',
  filters: {
    reference: '',
    customerId: null,
    startDate: null,
    endDate: null,
  },
};

// Slice COMPLET
const quoteSlice = createSlice({
  name: 'quote',
  initialState,
  reducers: {
    resetActionStatus: (state) => {
      state.actionStatus = 'idle';
    },
    openModal: (state) => {
      state.showModal = true;
    },
    closeModal: (state) => {
      state.showModal = false;
    },
    openDeleteModal: (state) => {
      state.showDeleteModal = true;
    },
    closeDeleteModal: (state) => {
      state.showDeleteModal = false;
    },
    clearError: (state) => {
      state.error = null;
    },
    setSelectedQuote: (state, action) => {
      state.selectedQuote = action.payload;
    },
    setSearchQuery: (state, action) => {
      state.searchQuery = action.payload;
    },
    clearSearchResults: (state) => {
      state.searchResults = [];
      state.searchQuery = '';
    },
    setQuoteFilters: (state, action) => {
      const { reference, customerId, startDate, endDate } = action.payload;
      state.filters.reference = reference ?? '';
      state.filters.customerId = customerId ?? null;
      state.filters.startDate = startDate ?? null;
      state.filters.endDate = endDate ?? null;
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchQuotes
      .addCase(fetchQuotes.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchQuotes.fulfilled, (state, action) => {
        state.loading = false;
        state.actionStatus = 'idle';
        state.quotes = Array.isArray(action.payload) ? action.payload : (action.payload?.items || []);
        state.totalCount = action.payload?.totalCount || (Array.isArray(action.payload) ? action.payload.length : 0);
      })
      .addCase(fetchQuotes.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // fetchQuoteById -> loader séparé
      .addCase(fetchQuoteById.pending, (state) => {
        state.loadingById = true;
        state.error = null;
      })
      .addCase(fetchQuoteById.fulfilled, (state, action) => {
        state.loadingById = false;
        state.selectedQuote = action.payload;

        const incoming = action.payload;
        const index = state.quotes.findIndex((q) => q.quoteId === incoming.quoteId);
        if (index !== -1) {
          state.quotes[index] = { ...state.quotes[index], ...incoming };
        }
      })
      .addCase(fetchQuoteById.rejected, (state, action) => {
        state.loadingById = false;
        state.error = action.payload;
      })

      // createQuote
      .addCase(createQuote.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createQuote.fulfilled, (state, action) => {
        state.loading = false;
        state.quotes.push(action.payload);
        state.totalCount += 1;
        state.showModal = false;
      })
      .addCase(createQuote.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // updateQuote
      .addCase(updateQuote.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateQuote.fulfilled, (state, action) => {
        state.loading = false;
        state.quotes = state.quotes.map((quote) => {
          if (quote.quoteId === action.payload.quoteId) return action.payload;
          return quote;
        });
        state.showModal = false;
      })
      .addCase(updateQuote.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // softDeleteQuote
      .addCase(softDeleteQuote.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(softDeleteQuote.fulfilled, (state, action) => {
        state.loading = false;
        state.quotes = state.quotes.filter((quote) => quote.quoteId !== action.payload);
        state.totalCount -= 1;
        state.showDeleteModal = false;
      })
      .addCase(softDeleteQuote.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // hardDeleteQuote
      .addCase(hardDeleteQuote.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(hardDeleteQuote.fulfilled, (state, action) => {
        state.loading = false;
        state.quotes = state.quotes.filter((quote) => quote.quoteId !== action.payload);
        state.totalCount -= 1;
        state.showDeleteModal = false;
      })
      .addCase(hardDeleteQuote.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // autres fetchs liste
      .addCase(fetchActiveQuotes.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchActiveQuotes.fulfilled, (state, action) => {
        state.loading = false;
        state.quotes = Array.isArray(action.payload) ? action.payload : (action.payload?.items || []);
        state.totalCount = action.payload?.totalCount || (Array.isArray(action.payload) ? action.payload.length : 0);
      })
      .addCase(fetchActiveQuotes.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(fetchInactiveQuotes.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchInactiveQuotes.fulfilled, (state, action) => {
        state.loading = false;
        state.quotes = Array.isArray(action.payload) ? action.payload : (action.payload?.items || []);
        state.totalCount = action.payload?.totalCount || (Array.isArray(action.payload) ? action.payload.length : 0);
      })
      .addCase(fetchInactiveQuotes.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(fetchQuotesByCustomerId.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchQuotesByCustomerId.fulfilled, (state, action) => {
        state.loading = false;
        state.quotes = Array.isArray(action.payload) ? action.payload : (action.payload?.items || []);
        state.totalCount = action.payload?.totalCount || (Array.isArray(action.payload) ? action.payload.length : 0);
      })
      .addCase(fetchQuotesByCustomerId.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(fetchQuotesByCustomerName.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchQuotesByCustomerName.fulfilled, (state, action) => {
        state.loading = false;
        state.quotes = Array.isArray(action.payload) ? action.payload : (action.payload?.items || []);
        state.totalCount = action.payload?.totalCount || (Array.isArray(action.payload) ? action.payload.length : 0);
      })
      .addCase(fetchQuotesByCustomerName.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(fetchQuotesByDateRange.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchQuotesByDateRange.fulfilled, (state, action) => {
        state.loading = false;
        state.quotes = Array.isArray(action.payload) ? action.payload : (action.payload?.items || []);
        state.totalCount = action.payload?.totalCount || (Array.isArray(action.payload) ? action.payload.length : 0);
      })
      .addCase(fetchQuotesByDateRange.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(fetchQuotesPaginated.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchQuotesPaginated.fulfilled, (state, action) => {
        state.loading = false;
        state.quotes = action.payload.items || [];
        state.totalCount = action.payload.totalCount || 0;
      })
      .addCase(fetchQuotesPaginated.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // actions métier
      .addCase(publishQuote.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(publishQuote.fulfilled, (state, action) => {
        state.loading = false;
        state.actionStatus = 'succeeded';
        state.quotes = state.quotes.map((quote) =>
          quote.quoteId === action.payload.id
            ? { ...quote, ...action.payload }
            : quote
        );
      })
      .addCase(publishQuote.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(approveQuote.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(approveQuote.fulfilled, (state, action) => {
        state.loading = false;
        state.actionStatus = 'succeeded';
        state.quotes = state.quotes.map((quote) =>
          quote.quoteId === action.payload.id
            ? { ...quote, ...action.payload }
            : quote
        );
      })
      .addCase(approveQuote.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(cancelQuote.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(cancelQuote.fulfilled, (state, action) => {
        state.loading = false;
        state.actionStatus = 'succeeded';
        state.quotes = state.quotes.map((quote) =>
          quote.quoteId === action.payload.id
            ? { ...quote, ...action.payload }
            : quote
        );
      })
      .addCase(cancelQuote.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(transformQuoteToOrder.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(transformQuoteToOrder.fulfilled, (state, action) => {
        state.loading = false;
        state.actionStatus = 'succeeded';
        const quote = state.quotes.find((q) => q.quoteId === action.payload.id);
        if (!quote) return;
        if (!quote.quoteIsOrdered) {
          quote.quoteIsOrdered = true;
        }
      })
      .addCase(transformQuoteToOrder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const {
  openModal,
  closeModal,
  openDeleteModal,
  closeDeleteModal,
  clearError,
  setSelectedQuote,
  setSearchQuery,
  clearSearchResults,
  resetActionStatus,
  setQuoteFilters,
} = quoteSlice.actions;

export default quoteSlice.reducer;